import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, interval, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AddOnDetail } from '../classes/add-on-detail';
import { AppConfiguration } from '../classes/configuration';
import { Dispatch } from '../classes/dispatch';
import { DispatchDriverSummary } from '../classes/dispatch-driver-summary';
import { DispatchFilterParams } from '../classes/dispatch-filter-params';
import { DispatchPassenger } from '../classes/dispatch-passenger';
import { DispatchTaskDetails } from '../classes/dispatch-task-details';
import { DispatchTaskGroupSummary } from '../classes/dispatch-task-group-summary';
import { DispatchUpdatePayload } from '../classes/dispatch-update-payload';
import { DispatchUpdateResponse } from '../classes/dispatch-update-response';
import { Passenger } from '../classes/passenger';
import { TaskType } from '../classes/task-type-enum';
import { DISPATCH_PASSENGER_URL, DISPATCH_WORKER_TIMER, TASK_DETAILS_URL, TASK_URL } from '../common/constants';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { MsalConfigService } from './msal-config.service';
import { ToastService } from './toast.service';

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;
const worker = new Worker(
  new URL('../workers/dispatch.worker', import.meta.url)
);
@Injectable({
  providedIn: 'root'
})
export class DispatchService {
  private dispatchData$: BehaviorSubject<Dispatch> = new BehaviorSubject(null);
  interval: Subscription = new Subscription();
  dispatchURL: string;
  hostApi: string = '';
  dispatchFilterFormVals: any;
  dispatchFilterParams = new BehaviorSubject<DispatchFilterParams>(undefined);
  isDispatchFilterOn = false;

  constructor(
    private logger: LoggerService,
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private toastService: ToastService,
    private msalConfigService: MsalConfigService
  ) {
    this.subscribeToConfiguration();
  }

  subscribeToConfiguration(): void {
    this.configurationService
      .getConfiguration()
      .subscribe((configuration: AppConfiguration) => {
        if (configuration) {
          this.hostApi = configuration.HostApi;
          this.dispatchURL = configuration.HostApi.concat('/cvv/dispatch');
        }
      });
  }

  private getUrl(path: string): string {
    return this.hostApi + path;
  }

  updateDispatchData(data: Dispatch): void {
    this.dispatchData$.next(data);
  }

  getDispatchData(): Observable<Dispatch> {
    return this.dispatchData$.asObservable();
  }

  // toggle API threading
  public startDispatchInterval(): void {
    this.triggerDispatchUpdate();
    this.interval = interval(DISPATCH_WORKER_TIMER).subscribe(() => {
      this.triggerDispatchUpdate();
    });
  }

  public triggerDispatchUpdate(): void {
    if (useImMemoryWebApi) {
      this.runMainThread();
    } else {
      this.runWorkerThread();
    }
  }

  public stopWorkerInterval(): void {
    this.interval.unsubscribe();
  }

  // Web Worker Thread PUT
  public runWorkerThread(): any {
    this.logger.debug('Dispatch called on worker thread', 'worker');
    worker.onmessage = ({ data }) => {
      if (data.error) {
        this.logger.error(`Dispatch worker error: ${data.message} (status: ${data.status})`);
        return;
      }
      this.updateDispatchData(JSON.parse(data.data));
      return JSON.parse(data.data);
    };
    worker.onerror = (err) => {
      this.logger.error('Dispatch worker thread error', err);
    };

    this.msalConfigService.getAzureToken().subscribe((token: string) => {
      const params: DispatchFilterParams = this.dispatchFilterParams.getValue() || {
        driver: '',
        date: '',
        hoursOffset: 0,
        searchText: '',
        type: ''
      };
      worker.postMessage({
        url: this.dispatchURL,
        params,
        timeout: null,
        payload: null,
        authHeader: `Bearer ${token}`
      });
    });
  }

  // Main Thread PUT... used when inMemoryWebApi = true
  public runMainThread(): void {
    this.logger.debug('Dispatch called on main thread', 'worker');
    const noCache = new Date().getTime();
    const headers = { 'Content-type': 'application/json; charset=utf-8' };
    this.http
      .get<Dispatch>('assets/data/mock-data/dispatch-get-response1.json' + '?noCache=' + noCache, { headers: headers })
      .subscribe((data) => {
        this.updateDispatchData(data);
        this.logger.debug(`GET Dispatch response:`, data, 'http');
      });
  }

  public getTaskDetails(
    taskId: number | string,
    taskDetailType: string
  ): Observable<DispatchUpdateResponse | DispatchTaskDetails> {
    const noCache = new Date().getTime();
    const url = `${this.getUrl(TASK_DETAILS_URL)}/${taskId}?taskDetailType=${taskDetailType}&_${noCache}`;
    return this.http
      .get<DispatchUpdateResponse | DispatchTaskDetails>(url, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'my-auth-token' })
      })
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleError(error, `Failed to get Task Details for id ${taskId}`))
      );
  }

  public dropAssignTask(task: DispatchTaskGroupSummary, driver: DispatchDriverSummary): Observable<any> {
    const body: any = {
      driverId: driver.driverId,
      taskId: task.flightId,
      taskTypeId: task.taskType
    };

    return this.http.post(`${this.getUrl(TASK_DETAILS_URL)}/${task.flightId || task.taskName}`, body).pipe(
      map((response: any[]) => {
        if (response && response.length) {
          return response[0];
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to assign task'))
    );
  }

  public deletePassengerOrAddOnItem(
    item: Passenger | AddOnDetail,
    task: DispatchTaskDetails,
    itemType: 'addon' | 'passenger'
  ): Observable<DispatchUpdateResponse> {
    return this.http.delete<DispatchUpdateResponse>(this.getUrl(DISPATCH_PASSENGER_URL), {
      params: {
        flightId: `${task.metadata.flightId}`,
        itemId: `${item.id}`,
        itemType,
        taskType: task.taskTypeName,
        taskName: task.taskName
      }
    }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to delete passenger/add-on item'))
    );
  }

  public movePassengerOrAddOnItem(
    item: Passenger | AddOnDetail,
    fromTask: DispatchTaskDetails,
    toTask: DispatchTaskDetails,
    itemType: 'addon' | 'passenger'
  ): Observable<any> {
    const body = {
      driverId: (toTask && toTask.assignedTo) || 'unassigned',
      flightSegId: toTask?.metadata?.flightId || fromTask?.metadata?.flightId,
      itemId: item && item.id,
      itemType,
      taskNameFrom: (fromTask && fromTask.taskName) || 'unassigned',
      taskNameTo: (toTask && toTask.taskName) || 'unassigned',
      taskTypeFrom: (fromTask && fromTask.taskTypeName) || 'unassigned',
      taskTypeTo: (toTask && toTask.taskTypeName) || 'unassigned'
    };

    return this.http.post(this.getUrl(DISPATCH_PASSENGER_URL), body).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to move passenger/add-on item'))
    );
  }

  public updateTaskTime(
    time: string,
    taskDetails: DispatchTaskDetails,
    updateType: 'pickupTime' | 'dropoffTime' | 'plannedStartTime' | 'plannedEndTime'
  ): Observable<DispatchUpdateResponse> {
    const updatePayload: DispatchUpdatePayload = {
      flightId: `${taskDetails.metadata?.flightId ?? ''}`,
      taskId: `${taskDetails.taskTypeName === TaskType.Other ? taskDetails.taskName : taskDetails.taskId}`,
      taskName: taskDetails.taskName,
      taskType: taskDetails.taskTypeName,
      time,
      updateType
    };
    return this.http.post<DispatchUpdateResponse>(`${this.getUrl(TASK_DETAILS_URL)}/task`, updatePayload).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to update task time'))
    );
  }

  public assignDriverFromDropdown(
    driverId: string | number,
    taskDetails: DispatchTaskDetails
  ): Observable<DispatchUpdateResponse> {
    return this.updateTaskDetails({
      driverId: `${driverId}`,
      flightId: `${taskDetails.metadata?.flightId ?? ''}`,
      taskId: `${taskDetails.taskId}`,
      taskName: taskDetails.taskName,
      taskType: taskDetails.taskTypeName,
      updateType: 'assignTaskFromDriverDropdown'
    });
  }

  private updateTaskDetails(updatePayload: DispatchUpdatePayload): Observable<DispatchUpdateResponse> {
    return this.http.put<DispatchUpdateResponse>(`${this.getUrl(TASK_DETAILS_URL)}/${updatePayload.taskName}`, updatePayload).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to update task details'))
    );
  }




  public addPassengerOrAddOnItem(
    flightId: number,
    dispatchPassenger: DispatchPassenger
  ): Observable<DispatchUpdateResponse> {
    const url = `${this.getUrl(DISPATCH_PASSENGER_URL)}/${flightId}`;
    return this.http
      .put<DispatchUpdateResponse>(url, dispatchPassenger)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, `Failed to add dispatch passenger or add-on details for flight id ${flightId}`)
        )
      );
  }

  endRemoteParkingTask(task: DispatchTaskGroupSummary, deleteBy: string): Observable<any> {
    return this.http
      .put<any>(`${this.getUrl(TASK_URL)}/${task.taskName}`, {
        deleteBy,
        driverNote: '',
        taskId: task.taskName,
        timeStamp: moment.utc(),
        type: 'endTask'
      })
      .pipe(
        tap(() => this.triggerDispatchUpdate()),
        catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to end remote parking task'))
      );
  }

  public saveFlightTaskCoordNotes(task: DispatchTaskGroupSummary, taskDetailsList: DispatchTaskDetails[], notes: string): Observable<any> {
    const body: any = {
      flightId: task.flightId,
      notes: notes,
      taskId: taskDetailsList[0].taskId,
      taskName: task.taskName,
      taskType: task.taskType,
      updateType: "taskNotes",
    };

    return this.http.post(`${this.getUrl(TASK_DETAILS_URL)}/task`, body).pipe(
      map((response: any[]) => {
        if (response && response.length) {
          return response[0];
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to save coordinator notes'))
    );
  }

  private handleError(error: HttpErrorResponse, message: string) {
    if (error.status === 409 && error.error?.reason === 'DRIVER_HAS_NO_VAN') {
      this.toastService.show('Selected driver does not have a van assigned.', { classname: 'bg-danger text-light', delay: 8000 });
    } else {
      this.toastService.show('HTTP Error Occurred!', { classname: 'bg-danger text-light', delay: 8000 });
    }
    if (error.status === 0) {
      this.logger.error(`${message}:`, error.error);
    } else {
      this.logger.error(`${message}: ${error.status}, body was: ${error.error}`);
    }
    return throwError(error);
  }
}
